import React from "react"
import { Box, Container, Stack, Typography } from "@mui/material"
import { useGallery } from "@/features/dashboard/hooks/useGallery"
import ProductCard from "@/features/dashboard/components/ProductCard"
import GenerateWithAI from "@/features/dashboard/components/GenerateWithAI"
import ImageGallery from "@/shared/components/image-gallery"
import { usePromptCatalog } from "@/shared/hooks/usePromptCatalog"
import { useImageGalleryViewer } from "@/shared/hooks/useImageGalleryViewer"
import { useCustomPrompts } from "@/shared/hooks/useCustomPrompts"
import { useAuth } from "@/features/auth/context/AuthContext"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import EmptyUploadPlaceholder from "@/features/dashboard/components/EmptyUploadPlaceholder"
import CreditProgressBar from "@/features/dashboard/components/CreditProgressBar"
import { useCreditProgress } from "@/shared/hooks/useCreditProgress"

// Cropper and its stylesheet are only needed after the editor is opened.
const ImageEditor = React.lazy(() => import("@/shared/components/image-editor"))

// Fixed per-product prompt cap — users can select up to this many prompts per
// product regardless of their credit balance.
const MAX_PROMPTS_PER_PRODUCT = 5

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const { showSnackbar } = useSnackbar()
  const {
    sections,
    fileInputRef,
    replaceFileInputRef,
    editorState,
    handleUploadClick,
    handleFileChange,
    handleDroppedFiles,
    handleReplaceFileChange,
    handleFieldChange,
    handleKeywordsChange,
    handlePromptToggle,
    handleOpenEditor,
    handleEditorSave,
    handleEditorClose,
    handleReplaceImage,
    handleRemoveSection,
    generatingTextFor,
    handleGenerateText,
    handleRemovePromptFromSections,
    resetSections
  } = useGallery(MAX_PROMPTS_PER_PRODUCT)
  const [dragActive, setDragActive] = React.useState(false)
  const dragDepthRef = React.useRef(0)

  const hasFilesInDragEvent = React.useCallback((event: React.DragEvent<HTMLElement>): boolean => {
    const transferTypes = event.dataTransfer?.types
    return Boolean(transferTypes && Array.from(transferTypes).includes("Files"))
  }, [])

  const resetDragState = React.useCallback(() => {
    dragDepthRef.current = 0
    setDragActive(false)
  }, [])

  React.useEffect(() => {
    window.addEventListener("drop", resetDragState)
    window.addEventListener("dragend", resetDragState)

    return () => {
      window.removeEventListener("drop", resetDragState)
      window.removeEventListener("dragend", resetDragState)
    }
  }, [resetDragState])

  const { prompts, loading: promptsLoading, error: promptsError } = usePromptCatalog()
  const {
    prompts: customPrompts,
    error: customPromptsError,
    saving: customPromptSaving,
    savePrompt,
    deletePrompt
  } = useCustomPrompts(user?.uid ?? null)

  const mergedPrompts = React.useMemo(() => {
    return [...customPrompts, ...prompts]
  }, [prompts, customPrompts])

  const { viewer, openWithImages, closeViewer } = useImageGalleryViewer()
  const {
    creditsRemaining,
    creditLimit,
    creditsToBeUsed,
    creditsAfterUse,
    isLoading: creditLoading
  } = useCreditProgress(sections)
  const handleSaveCustomPrompt = async (promptText: string): Promise<string> => {
    const savedPrompt = await savePrompt(promptText)
    return savedPrompt.id
  }

  const handleDeleteCustomPrompt = async (promptId: string): Promise<void> => {
    await deletePrompt(promptId)
    handleRemovePromptFromSections(promptId)
    showSnackbar("Prompt deleted.", "success")
  }

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    if (!hasFilesInDragEvent(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current += 1
    setDragActive(true)
  }

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!hasFilesInDragEvent(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "copy"
    setDragActive(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    if (!hasFilesInDragEvent(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) {
      setDragActive(false)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    if (!hasFilesInDragEvent(event)) return
    event.preventDefault()
    event.stopPropagation()
    resetDragState()
    handleDroppedFiles(event.dataTransfer.files)
  }

  return (
    <Container
      maxWidth={false}
      sx={{ position: "relative" }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            My Products
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            aria-label="Upload product images (one image per section)"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <input
            ref={replaceFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleReplaceFileChange}
          />
        </Stack>

        <Stack gap={3}>
          {sections.length > 0 && (
            <CreditProgressBar
              creditsRemaining={creditsRemaining}
              creditLimit={creditLimit}
              creditsToBeUsed={creditsToBeUsed}
              creditsAfterUse={creditsAfterUse}
              isLoading={creditLoading}
            />
          )}
          {sections.map((section, sectionIndex) => (
            <ProductCard
              key={section.id}
              onView={(images, initialIndex) => openWithImages(images, initialIndex)}
              section={section}
              sectionIndex={sectionIndex}
              maxSelected={MAX_PROMPTS_PER_PRODUCT}
              prompts={mergedPrompts}
              promptsLoading={promptsLoading}
              promptsError={promptsError}
              customPromptSaving={customPromptSaving}
              customPromptError={customPromptsError}
              onFieldChange={handleFieldChange}
              onKeywordsChange={handleKeywordsChange}
              onPromptToggle={handlePromptToggle}
              onSaveCustomPrompt={handleSaveCustomPrompt}
              onDeleteCustomPrompt={handleDeleteCustomPrompt}
              onOpenEditor={handleOpenEditor}
              onReplaceImage={handleReplaceImage}
              onRemoveSection={handleRemoveSection}
              generatingText={generatingTextFor === section.id}
              textGenerationBusy={generatingTextFor !== null}
              onGenerateText={handleGenerateText}
            />
          ))}
        </Stack>
        <EmptyUploadPlaceholder
          onUploadClick={handleUploadClick}
          title={sections.length === 0 ? "No products uploaded yet" : "Upload more products"}
        />
      </Stack>
      {sections.length > 0 && <GenerateWithAI sections={sections} onReset={resetSections} />}
      {dragActive && (
        <Box
          aria-live="polite"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: theme => theme.zIndex.modal,
            pointerEvents: "none",
            bgcolor: "rgba(25, 118, 210, 0.12)",
            border: "2px dashed",
            borderColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2
          }}>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            Drop images to upload
          </Typography>
        </Box>
      )}

      {editorState && sections.find(s => s.id === editorState.sectionId) && (
        <React.Suspense fallback={null}>
          <ImageEditor
            imageUrl={sections.find(s => s.id === editorState.sectionId)!.images[editorState.imageIndex]}
            onSave={handleEditorSave}
            onClose={handleEditorClose}
          />
        </React.Suspense>
      )}
      {viewer && <ImageGallery items={viewer.items} initialIndex={viewer.initialIndex} onClose={closeViewer} />}
    </Container>
  )
}

export default DashboardPage
